import { useEffect, useRef, useState } from "react";
import {
  ArrowRightIcon,
  BellIcon,
  CheckCheckIcon,
  ClockIcon,
  FileTextIcon,
  RotateCwIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { useNotificationNavigate } from "@/hooks";
import { useCompanyStore } from "@/Store";
import { formatRelativeTime } from "@/Utils";
import type { INotificationItem } from "@/types/INotification";
import NotificationTypeIcon from "@/Components/NotificationTypeIcon";
import S from "./styles.module.scss";

interface IExpiringDoc {
  id: number;
  name: string;
  status: "expired" | "expiring";
  daysLeft: number;
}

interface ITaskRisk {
  id: number;
  title: string;
  project: string | null;
}

const Notification = () => {
  const { getRequest, patchRequest } = useApi();
  const openNotification = useNotificationNavigate();
  const companies = useCompanyStore((state) => state.companies);
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState<IExpiringDoc[]>([]);
  const [tasks, setTasks] = useState<ITaskRisk[]>([]);
  const [items, setItems] = useState<INotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const loadPersistent = () => {
    setError(false);
    getRequest("/api/notification?limit=10")
      .then((r) => r.json())
      .then((j) => setItems(j.items ?? []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    getRequest("/api/notification/unread-count")
      .then((r) => r.json())
      .then((j) => setUnread(j.response?.count ?? 0))
      .catch(() => {});
  };

  useEffect(() => {
    let active = true;
    getRequest("/api/document/expiring?days=30")
      .then((r) => r.json())
      .then((j) => {
        if (active) setDocs(j.response ?? []);
      })
      .catch(() => {});
    // Tarefas atrasadas vêm do dashboard (só admin); ignora 403 dos demais.
    getRequest("/api/dashboard/overview")
      .then((r) => r.json())
      .then((j) => {
        if (active) setTasks(j.response?.tasksAtRisk ?? []);
      })
      .catch(() => {});
    loadPersistent();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const handleItemClick = (item: INotificationItem) => {
    if (!item.read) {
      patchRequest(`/api/notification/${item.id}/read`, {}).catch(() => {});
      setItems((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, read: true } : n))
      );
      setUnread((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    openNotification(item);
  };

  const handleMarkAll = () => {
    patchRequest("/api/notification/read-all", {}).catch(() => {});
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const total = docs.length + tasks.length + unread;
  const isEmpty =
    !loading &&
    !error &&
    items.length === 0 &&
    docs.length === 0 &&
    tasks.length === 0;

  return (
    <div className={S.wrapper} ref={ref}>
      <button
        type="button"
        className={`${S.bell} ${open ? S.bellOpen : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={
          total ? `Notificações: ${total} pendência(s)` : "Notificações"
        }
      >
        <BellIcon size={20} />
        {total > 0 && (
          <span className={S.badge}>{total > 9 ? "9+" : total}</span>
        )}
      </button>

      {open && (
        <div className={S.panel} aria-label="Notificações">
          <div className={S.panelHeader}>
            <div className={S.panelTitle}>
              <span>Notificações</span>
              {unread > 0 && <span className={S.unreadCount}>{unread}</span>}
            </div>
            {unread > 0 && (
              <button
                type="button"
                className={S.markAllBtn}
                onClick={handleMarkAll}
              >
                <CheckCheckIcon size={14} aria-hidden="true" />
                Marcar todas como lidas
              </button>
            )}
          </div>

          <div className={S.panelBody}>
            {isEmpty ? (
              <div className={S.empty}>
                <span className={S.emptyIcon}>
                  <CheckCheckIcon size={22} aria-hidden="true" />
                </span>
                <strong>Tudo em dia</strong>
                <p>Você não tem notificações por aqui.</p>
              </div>
            ) : (
              <>
                <div className={S.section}>
                  <span className={S.sectionTitle}>Recentes</span>
                  {loading &&
                    [0, 1, 2].map((key) => (
                      <div key={key} className={S.skeleton} aria-hidden>
                        <span className={S.skeletonIcon} />
                        <span className={S.skeletonLines}>
                          <span />
                          <span />
                        </span>
                      </div>
                    ))}
                  {error && !loading && (
                    <button
                      type="button"
                      className={S.retry}
                      onClick={() => {
                        setLoading(true);
                        loadPersistent();
                      }}
                    >
                      <RotateCwIcon size={14} aria-hidden="true" />
                      Não foi possível carregar. Tentar novamente.
                    </button>
                  )}
                  {!loading && !error && items.length === 0 && (
                    <p className={S.stateMsg}>Nenhuma notificação recente.</p>
                  )}
                  {!loading &&
                    !error &&
                    items.map((n) => {
                      const companyName = companies.find(
                        (c) => c.id === n.companyId
                      )?.name;
                      return (
                        <button
                          type="button"
                          key={`n${n.id}`}
                          className={`${S.item} ${n.read ? S.itemRead : ""}`}
                          onClick={() => handleItemClick(n)}
                        >
                          <NotificationTypeIcon type={n.type} muted={n.read} />
                          <span className={S.itemBody}>
                            <span className={S.itemTop}>
                              <span className={S.itemName} title={n.title}>
                                {n.title}
                              </span>
                              <time
                                className={S.itemTime}
                                dateTime={n.createdAt}
                              >
                                {formatRelativeTime(n.createdAt)}
                              </time>
                            </span>
                            <span className={S.itemMessage} title={n.message}>
                              {n.message}
                            </span>
                            {companyName && (
                              <span className={S.itemCompany}>
                                {companyName}
                              </span>
                            )}
                          </span>
                          {!n.read && (
                            <span
                              className={S.unreadMark}
                              aria-label="Não lida"
                            />
                          )}
                        </button>
                      );
                    })}
                </div>

                {docs.length > 0 && (
                  <div className={S.section}>
                    <span className={S.sectionTitle}>Documentos a vencer</span>
                    {docs.slice(0, 5).map((d) => (
                      <Link
                        key={`d${d.id}`}
                        to="/documentos"
                        className={S.item}
                        onClick={() => setOpen(false)}
                      >
                        <span
                          className={`${S.itemIcon} ${
                            d.status === "expired" ? S.danger : S.warning
                          }`}
                        >
                          <FileTextIcon size={16} aria-hidden="true" />
                        </span>
                        <span className={S.itemBody}>
                          <span className={S.itemTop}>
                            <span className={S.itemName} title={d.name}>
                              {d.name}
                            </span>
                            <span
                              className={`${S.itemTag} ${
                                d.status === "expired" ? S.danger : S.warning
                              }`}
                            >
                              {d.status === "expired"
                                ? "Vencido"
                                : d.daysLeft === 0
                                  ? "Vence hoje"
                                  : `${d.daysLeft} dias`}
                            </span>
                          </span>
                        </span>
                      </Link>
                    ))}
                  </div>
                )}

                {tasks.length > 0 && (
                  <div className={S.section}>
                    <span className={S.sectionTitle}>Tarefas atrasadas</span>
                    {tasks.slice(0, 5).map((t) => (
                      <Link
                        key={`t${t.id}`}
                        to="/projetos"
                        className={S.item}
                        onClick={() => setOpen(false)}
                      >
                        <span className={`${S.itemIcon} ${S.danger}`}>
                          <ClockIcon size={16} aria-hidden="true" />
                        </span>
                        <span className={S.itemBody}>
                          <span className={S.itemName} title={t.title}>
                            {t.title}
                          </span>
                          {t.project && (
                            <span className={S.itemMessage}>{t.project}</span>
                          )}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <Link
            to="/notificacoes"
            className={S.footerLink}
            onClick={() => setOpen(false)}
          >
            Ver todas as notificações
            <ArrowRightIcon size={14} aria-hidden="true" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default Notification;

